// components/CardStockRank/index.jsx

import { Buildings } from '@phosphor-icons/react';
import ReactApexChart from 'react-apexcharts';
import { Tooltip } from 'antd';
import styled from 'styled-components';

const Container = styled.div`
  width: auto;
  border: 1px solid #d1d8e0;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  cursor: pointer;
`;

const ImageContent = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #5a9bd8;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const Content = styled.div`
  padding: 1rem;
  height: auto;

  .contentGraphicValue {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    height: auto;
    width: auto;
  }

  h6 {
    position: absolute;
    font-size: 1.2rem;
    font-weight: 700;
    color: #1d2d3a;
  }

  h5 {
    text-align: center;
    font-size: 1rem;
    color: #40332f;
  }
`;

export function CardStockRank({ stock, selectedActive }) {
  const kpi = stock?.latest_kpi || stock?.kpi || stock?.kpis || {};

  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  const clamp = (value, min = 0, max = 100) => {
    return Math.min(Math.max(value, min), max);
  };

  const netMargin = toNumber(kpi.net_margin);
  const ebitdaMargin = toNumber(kpi.ebitda_margin);
  const dividendYield = toNumber(kpi.dy ?? kpi.avg_dy);
  const netDebt = toNumber(kpi.net_debt);
  const netDebtEbitda = toNumber(kpi.net_debt_ebitda);

  // Preferência: fatias reais do score (score_items) na mesma ordem dos
  // rótulos do donut [Lucro Líquido, EBITDA, Dividendos, Dívida Líquida,
  // Dívida Líquida EBITDA]. Fallback para a heurística de KPIs quando o ativo
  // ainda não tem score_items calculado no backend.
  const scoreItems = stock?.score_items || null;

  const rawValues = scoreItems
    ? [
        toNumber(scoreItems.net_income_score),
        toNumber(scoreItems.ebitda_score),
        toNumber(scoreItems.dividend_score),
        toNumber(scoreItems.net_debt_score),
        toNumber(scoreItems.net_debt_ebitda_score),
      ]
    : [
        netMargin !== null ? clamp(netMargin * 100) : 0,
        ebitdaMargin !== null ? clamp(ebitdaMargin * 100) : 0,
        dividendYield !== null ? clamp(dividendYield * 100) : 0,
        netDebt !== null ? clamp(netDebt <= 0 ? 100 : 50) : 0,
        netDebtEbitda !== null
          ? clamp(netDebtEbitda <= 0 ? 100 : 100 - netDebtEbitda * 20)
          : 0,
      ];

  const values = rawValues.map((value) => {
    if (value === null || value <= 0) return 2;
    return value;
  });

  const score = Number(stock?.score) || 0;
  const remaining = Math.max(
    100 * values.length - values.reduce((sum, value) => sum + value, 0),
    0
  );

  const labels = [
    'Lucro Líquido',
    'EBITDA',
    'Dividendos',
    'Dívida Líquida',
    'Dívida Líquida EBITDA',
    '',
  ];

  const compare = {
    series: [...values, remaining],
    options: {
      chart: {
        height: 150,
        type: 'donut',
      },
      stroke: { show: false },
      grid: {
        padding: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        },
      },
      legend: {
        show: false,
      },
      plotOptions: {
        pie: {
          startAngle: -90,
          endAngle: 90,
          donut: {
            size: '65%',
            labels: {
              show: false,
            },
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        y: {
          formatter: (val) => {
            if (val > 100) return '';
            return `${val.toFixed(0)}%`;
          },
        },
      },
      labels,
      colors: [
        '#38486B',
        '#635185',
        '#99538E',
        '#CD5485',
        '#F45F6A',
        '#E4E5E7',
      ],
    },
  };

  return (
    <Container
      onClick={() =>
        selectedActive({
          slug: stock.ticker,
          type: 'stock',
        })
      }
    >
      <header
        style={{
          background: '#e9eef3',
          padding: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <ImageContent>
          <Buildings size="1.5rem" weight="fill" />
        </ImageContent>

        <button
          type="button"
          style={{
            border: 'none',
            background: 'transparent',
            fontWeight: 600,
            color: '#1d2d3a',
            cursor: 'pointer',
          }}
        >
          {stock.ticker}
        </button>
      </header>

      <Content>
        <div className="contentGraphicValue">
          <ReactApexChart
            options={compare.options}
            series={[...values, remaining]}
            type="donut"
            height={200}
            width={250}
          />

          <Tooltip
            title={
              scoreItems
                ? 'Nota (0–100) calculada a partir dos fundamentos da empresa'
                : 'Nota calculada com base nos KPIs disponíveis da empresa'
            }
          >
            <h6>{score.toFixed(0)}%</h6>
          </Tooltip>
        </div>

        <div>
          <h5 title={stock.name}>{stock.name}</h5>
        </div>
      </Content>
    </Container>
  );
}